import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import toast from "react-hot-toast";
import type { Rsp } from "@repo/types";
import { Button, Spinner } from "@heroui/react";
import { AuthAPI } from "@repo/api/auth";
import { UserAPI } from "@repo/api/user";
import { PhoneInput } from "@/components/from/phone-input";
import { useDocumentTitle } from "@/hooks/useDocumentTitle.ts";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { asSearchString } from "@repo/utils/search-params";
import { useAuth } from "@repo/context/auth/auth";

export type UpdatePhoneSearch = {
  session_code?: string;
  uid?: string;
  biz?: string;
  redirect?: string;
};

export const Route = createFileRoute("/user/account/updatePhone")({
  validateSearch: (search: Record<string, unknown>): UpdatePhoneSearch => ({
    session_code: asSearchString(search.session_code),
    uid: asSearchString(search.uid),
    biz: asSearchString(search.biz),
    redirect: asSearchString(search.redirect),
  }),
  component: UpdatePhone,
});

"use client";

export default function UpdatePhone() {
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [smsCode, setSmsCode] = useState<string>("");
  const [countryCode] = useState<string>("86"); // 默认中国区号+86
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { session_code: sessionCode, uid } = getRouteApi("/user/account/updatePhone").useSearch();
  const { userInfo } = useAuth();
  useDocumentTitle("绑定新号码");

  const handleGetSmsCode = () => {
    if (!phoneNumber) {
      toast.error("请输入手机号");
      return Promise.reject();
    }
    if (!countryCode) {
      toast.error("请选择国家区号");
      return Promise.reject();
    }
    return AuthAPI.getSMSCode(phoneNumber, countryCode, "update_phone").then(
      (res) => {
        if (res.status !== 0) {
          toast.error(res.msg || "验证码发送失败");
          return Promise.reject();
        }
        toast.success(`验证码发送成功`);
      },
      (err: Rsp<null>) => {
        toast.error(err.msg || "验证码发送失败");
        return Promise.reject();
      },
    );
  };
  const handleSubmit = () => {
    setLoading(true);
    if (!sessionCode) {
      toast.error("授权失效，请重新开始");
      setLoading(false);
      return;
    }

    const targetUid = uid || userInfo?.uid;
    if (!targetUid) {
      toast.error("无法获取用户信息");
      setLoading(false);
      return;
    }

    UserAPI.updatePhone(targetUid, phoneNumber, smsCode, countryCode, sessionCode)
      .then(
        () => {
          navigate({ to: "/result/success", search: { type: "success", title: "手机号更新成功", end: "true", op: "返回帮助中心", redirect: "/help" }, replace: true })
        },
        (err: Rsp<null>) => {
          return toast.error(err.msg);
        },
      )
      .finally(() => setLoading(false));
  };
  return (
    <div className="p-4">
      <div className={"text-3xl font-bold text-gray-800 mb-1 mt-4"}>绑定新手机号</div>
      <div className={"text-md text-gray-500"}>输入新手机号和验证码，完成绑定</div>
      <form className="my-6 flex flex-col gap-3" onSubmit={(e) => e.preventDefault()}>
        <PhoneInput
          value={phoneNumber}
          onChange={setPhoneNumber}
          onGetCode={handleGetSmsCode}
          codeValue={smsCode}
          onCodeChange={setSmsCode}
        />
        <Button className={"mt-4"} type="submit" isPending={loading} onPress={handleSubmit}>
          {({ isPending }) => (
            <>
              {isPending && <Spinner size="sm" color="current" />}
              确定
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
